<template>
  <div
    v-click-outside="closeFloatingPanel"
    class="shelf-skew-picker"
  >
    <button
      ref="trigger"
      class="shelf-skew-trigger"
      :class="{ 'is-open': isOpen, 'has-override': hasAnyOverride }"
      type="button"
      :title="`Shelf skew: ${formattedSkew}; product spacing: ${formattedProductSpacing}`"
      @click.stop="toggleFloatingPanel"
    >
      <i class="bi bi-arrow-clockwise" />
    </button>

    <transition name="shelf-skew-panel">
      <div
        v-if="isOpen"
        ref="panel"
        class="shelf-skew-panel"
        :style="floatingPanelStyle"
        role="dialog"
        aria-label="Shelf skew"
        @click.stop
      >
        <div class="shelf-skew-panel__head">
          <strong>Shelf skew</strong>
          <span>{{ formattedSkew }}</span>
        </div>

        <div class="shelf-skew-slider-wrap">
          <input
            class="shelf-skew-slider"
            type="range"
            :min="min"
            :max="max"
            :step="step"
            :value="skew"
            :style="sliderStyle"
            @input="onSlider"
          >
        </div>

        <div class="shelf-skew-panel__foot">
          <span>Overrides fixture perspective</span>
          <button
            type="button"
            :disabled="!hasSkewOverride"
            @click="inheritPerspective"
          >
            Inherit
          </button>
        </div>

        <div class="shelf-skew-panel__divider" />

        <div class="shelf-skew-panel__head">
          <strong>Product spacing</strong>
          <span>{{ formattedProductSpacing }}</span>
        </div>

        <div class="shelf-skew-slider-wrap">
          <input
            class="shelf-skew-slider"
            type="range"
            :min="spacingMin"
            :max="spacingMax"
            :step="spacingStep"
            :value="effectiveProductSpacing"
            :style="spacingSliderStyle"
            aria-label="Product spacing"
            @input="onSpacingSlider"
          >
        </div>

        <div class="shelf-skew-panel__foot">
          <span>Overrides canvas default</span>
          <button
            type="button"
            :disabled="!hasSpacingOverride"
            @click="inheritProductSpacing"
          >
            Inherit
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import fixedDropdownPosition from '@frontend/mixins/fixedDropdownPosition';
import {
  DEFAULT_PRODUCT_SPACING,
  MAX_SHELF_PRODUCT_SPACING,
  MIN_SHELF_PRODUCT_SPACING,
  normalizeShelfProductSpacing,
} from '@frontend/components/display-builder/layout-defaults';

export default {
  name: 'ShelfSkewPicker',
  directives: {
    clickOutside: {
      bind(el, binding) {
        el._shelfSkewOutsideHandler = (event) => {
          if (!el.contains(event.target)) {
            binding.value(event);
          }
        };
        document.addEventListener('mousedown', el._shelfSkewOutsideHandler);
      },
      unbind(el) {
        document.removeEventListener('mousedown', el._shelfSkewOutsideHandler);
      },
    },
  },
  mixins: [fixedDropdownPosition],
  floatingPanel: {
    gap: 7,
    viewportPadding: 8,
    rightOffset: 0,
  },
  props: {
    value: {
      type: Number,
      default: 0,
    },
    min: {
      type: Number,
      default: -15,
    },
    max: {
      type: Number,
      default: 15,
    },
    step: {
      type: Number,
      default: 0.5,
    },
    productSpacing: {
      type: Number,
      default: null,
    },
    canvasSpacing: {
      type: Number,
      default: DEFAULT_PRODUCT_SPACING,
    },
    spacingMin: {
      type: Number,
      default: MIN_SHELF_PRODUCT_SPACING,
    },
    spacingMax: {
      type: Number,
      default: MAX_SHELF_PRODUCT_SPACING,
    },
    spacingStep: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return {
      isOpen: false,
      skew: Number(this.value || 0),
      spacing: normalizeShelfProductSpacing(
        this.productSpacing,
        normalizeShelfProductSpacing(this.canvasSpacing, DEFAULT_PRODUCT_SPACING)
      ),
      spacingInherited: this.productSpacing === null || this.productSpacing === undefined,
    };
  },
  computed: {
    hasSkewOverride() {
      return Math.abs(this.skew) > Number.EPSILON;
    },
    hasSpacingOverride() {
      return !this.spacingInherited;
    },
    hasAnyOverride() {
      return this.hasSkewOverride || this.hasSpacingOverride;
    },
    formattedSkew() {
      return this.hasSkewOverride ? `${this.skew.toFixed(1)}°` : 'inherit';
    },
    normalizedCanvasSpacing() {
      return normalizeShelfProductSpacing(this.canvasSpacing, DEFAULT_PRODUCT_SPACING);
    },
    effectiveProductSpacing() {
      return this.spacingInherited ? this.normalizedCanvasSpacing : this.spacing;
    },
    formattedProductSpacing() {
      const value = `${this.effectiveProductSpacing.toFixed(0)}%`;

      return this.spacingInherited ? `inherit (${value})` : value;
    },
    sliderStyle() {
      const progress = (this.skew - this.min) / Math.max(1, this.max - this.min) * 100;

      return {
        background: `linear-gradient(to right, #2eb89a 0%, #2eb89a ${progress}%, #edf1f5 ${progress}%, #edf1f5 100%)`,
      };
    },
    spacingSliderStyle() {
      const progress = (this.effectiveProductSpacing - this.spacingMin)
        / Math.max(1, this.spacingMax - this.spacingMin) * 100;

      return {
        background: `linear-gradient(to right, #2eb89a 0%, #2eb89a ${progress}%, #edf1f5 ${progress}%, #edf1f5 100%)`,
      };
    },
  },
  watch: {
    value(value) {
      this.skew = Number(value || 0);
    },
    productSpacing(value) {
      this.spacingInherited = value === null || value === undefined;
      this.spacing = normalizeShelfProductSpacing(value, this.normalizedCanvasSpacing);
    },
    canvasSpacing() {
      if (this.spacingInherited) {
        this.spacing = this.normalizedCanvasSpacing;
      }
    },
  },
  methods: {
    onSlider(event) {
      this.skew = Number(event.target.value);
      this.$emit('change', this.skew);
    },
    inheritPerspective() {
      this.skew = 0;
      this.$emit('change', 0);
    },
    onSpacingSlider(event) {
      this.spacingInherited = false;
      this.spacing = normalizeShelfProductSpacing(
        event.target.value,
        this.normalizedCanvasSpacing
      );
      this.$emit('spacing-change', this.spacing);
    },
    inheritProductSpacing() {
      this.spacingInherited = true;
      this.spacing = this.normalizedCanvasSpacing;
      this.$emit('spacing-change', null);
    },
  },
};
</script>

<style scoped>
.shelf-skew-picker {
  position: relative;
  display: inline-flex;
}

.shelf-skew-trigger {
  border: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 9px;
  background: var(--bg);
  border-radius: var(--radius-sm);
  font-size: 12.5px;
  font-weight: 500;
  color: var(--muted);
  cursor: pointer;
  transition: background .14s, border-color .14s, color .14s, box-shadow .14s;
  line-height: 1;
}

.shelf-skew-trigger:hover {
  background: #f7f8fa;
  border-color: #c9cdd4;
  color: #1a1d23;
}

.shelf-skew-panel {
  position: fixed;
  z-index: 1100;
  width: 240px;
  padding: 12px 12px 10px;
  border: 1px solid #e4e7eb;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 8px 24px rgba(16, 24, 40, 0.14), 0 2px 6px rgba(16, 24, 40, 0.08);
  color: #1d2939;
}

.shelf-skew-panel__head,
.shelf-skew-panel__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.shelf-skew-panel__head {
  margin-bottom: 10px;
  font-size: 12px;
}

.shelf-skew-panel__divider {
  margin: 12px 0;
  border-top: 1px solid #e4e7eb;
}

.shelf-skew-panel__head span {
  color: #667085;
  font-size: 11px;
  font-variant-numeric: tabular-nums;
}

.shelf-skew-slider-wrap {
  display: flex;
  align-items: center;
  height: 20px;
}

.shelf-skew-slider {
  width: 100%;
  height: 4px;
  appearance: none;
  border-radius: 999px;
  outline: none;
  cursor: pointer;
}

.shelf-skew-slider::-webkit-slider-thumb {
  width: 16px;
  height: 16px;
  appearance: none;
  border: 2px solid #ffffff;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 0 0 1.5px #2eb89a, 0 2px 4px rgba(16, 24, 40, 0.18);
}

.shelf-skew-slider::-moz-range-thumb {
  width: 14px;
  height: 14px;
  border: 2px solid #2eb89a;
  border-radius: 50%;
  background: #ffffff;
}

.shelf-skew-panel__foot {
  margin-top: 8px;
  color: #98a2b3;
  font-size: 10px;
}

.shelf-skew-panel__foot button {
  padding: 2px 4px;
  border: 0;
  background: transparent;
  color: #344054;
  cursor: pointer;
  font-size: 10px;
  font-weight: 600;
}

.shelf-skew-panel__foot button:disabled {
  color: #c5cbd4;
  cursor: default;
}

.shelf-skew-panel-enter-active,
.shelf-skew-panel-leave-active {
  transition: opacity 0.14s ease, transform 0.14s ease;
}

.shelf-skew-panel-enter,
.shelf-skew-panel-leave-to {
  opacity: 0;
  transform: translateY(-4px) scale(0.98);
}
</style>
